// Daily Log — the core 30-second action on mobile.
// Single scrolling form with collapsible sections, auto-save indicator,
// sticky CTA at the bottom on mobile.

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AVATAR_TONES } from "../lib/avatarTones";
import type { HorseColour } from "../lib/avatarTones";
import { computePace, paceFraction, paceLabel, currentHour } from "../lib/hydration";
import { useIsMobile } from "../lib/useMediaQuery";
import { useCurrentUser } from "../lib/useCurrentUser";
import { useUserYard } from "../lib/yard";
import { useHorse, useDailyLog, saveDailyLog, todayId } from "../lib/dailyLogs";
import { useWeather } from "../lib/useWeather";
import type { Intensity } from "../utils/eheCalculator";
import type { Horse, DailyLog as DailyLogDoc } from "../lib/types";
import { MOCK_HORSES, MOCK_YARD, MOCK_NOW_HOUR } from "../lib/mockData";
import type { MockHorse, MockFeed } from "../lib/mockData";
import "../styles/dashboard.css";
import "../styles/daily-log.css";

// Wrap a Firestore Horse + today's log (if any) into the MockHorse shape
// the rest of the screen reads. Reused for the top bar / pace preview.
function horseToMock(horse: Horse, log: DailyLogDoc | null | undefined): MockHorse {
  const validColour = horse.colour && (horse.colour in AVATAR_TONES)
    ? (horse.colour as HorseColour)
    : "bay";
  return {
    id: horse.id,
    name: horse.name,
    colour: validColour,
    breed: horse.breed ?? "—",
    age: horse.age ?? 0,
    weightKg: horse.weightKg,
    waterL: log?.water ?? 0,
    targetL: log?.ehe?.targetL ?? Math.max(20, Math.round(horse.weightKg * 0.08)),
    lastLoggedMin: null,
    flags: {},
    history5: [null, null, null, null, null],
    today: null,
  };
}

// Build a form-ready LogState from a real Firestore log (or defaults).
function initialLogFromReal(horse: Horse | null | undefined, log: DailyLogDoc | null | undefined): LogState {
  return {
    waterL: log?.water ?? 0,
    ambientTempC: log?.ambientTempC ?? 18,
    ambientOverride: !!log?.ambientTempC,
    exercise: {
      mins: log?.exercise?.mins ?? 0,
      intensity: log?.exercise?.intensity ?? "medium",
    },
    feed: log?.feed?.length
      ? log.feed.map((f) => ({ type: f.type, kg: f.kg }))
      : horse?.baselineFeed?.length
        ? horse.baselineFeed.map((f) => ({ type: f.type, kg: f.kg }))
        : [{ type: "Hay", kg: 6 }],
    feedSameAsBaseline: !log,
    behaviour: (log?.behaviour as string) || "bright",
    droppings: {
      tag: log?.droppings?.tag ?? null,
      count: log?.droppings?.count ?? null,
    },
    notes: log?.notes ?? "",
  };
}

interface LogState {
  waterL: number;
  ambientTempC: number;
  ambientOverride: boolean;
  exercise: { mins: number; intensity: string };
  feed: MockFeed[];
  feedSameAsBaseline: boolean;
  behaviour: string;
  droppings: { tag: string | null; count: number | null };
  notes: string;
}

function initialLogFor(horse: MockHorse | null | undefined): LogState {
  const today = horse?.today;
  return {
    waterL: horse?.waterL || 0,
    ambientTempC: MOCK_YARD.weatherC || 22,
    ambientOverride: false,
    exercise: {
      mins: today?.exercise?.mins ?? 0,
      intensity: (today?.exercise?.intensity || "Medium").toLowerCase(),
    },
    feed: today?.feed?.length
      ? today.feed.map((f) => ({ type: f.type, kg: f.kg }))
      : [{ type: "Hay", kg: 6 }, { type: "Hard feed", kg: 2 }],
    feedSameAsBaseline: true,
    behaviour: (today?.behaviour || "Bright").toLowerCase(),
    droppings: {
      tag: today?.droppings ? today.droppings.toLowerCase().replace(/[\s/]+/g, "-") : null,
      count: today?.droppingsCount ?? null,
    },
    notes: today?.notes || "",
  };
}

// ---------- Icons --------------------------------------------------------
const Ic = {
  back: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  drop: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5 C 4.5 5, 3 6.5, 3 9 a4 4 0 0 0 8 0 c 0 -2.5 -1.5 -4 -4 -7.5z" fill="currentColor" /></svg>,
  therm: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5.5" y="1.5" width="3" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" /><circle cx="7" cy="11" r="2" fill="currentColor" /></svg>,
  exer: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="9" cy="3" r="1.5" fill="currentColor" /><path d="M3 12L6 8L8 9L11 6M6 8L7 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  feed: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C9 4 11 5 11 8a4 4 0 0 1-8 0c0-3 2-4 4-6.5z" fill="currentColor" opacity="0.35" /><path d="M7 12V8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  behav: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7C2 7 3 4 4.5 4S6.5 10 8 10s2-6 3.5-6S13 7 13 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  drops: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="4" cy="9.5" r="2.2" stroke="currentColor" strokeWidth="1.2" /><circle cx="10" cy="9.5" r="2.2" stroke="currentColor" strokeWidth="1.2" /><circle cx="7" cy="4.5" r="2.2" stroke="currentColor" strokeWidth="1.2" /></svg>,
  notes: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 11.5L11.5 2.5L13 4L4 13L2 13L2.5 11.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>,
  pencil: <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2.5 11.5L11.5 2.5L13 4L4 13L2 13L2.5 11.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>,
  check: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7.5L6 10L11 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  chev: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M4 5L7 8L10 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  minus: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
};

// ---------- Top bar ------------------------------------------------------
function DLogTopBar({ horse }: { horse: MockHorse }) {
  const navigate = useNavigate();
  return (
    <div className="topbar dlog-topbar">
      <div className="topbar-inner">
        <button className="icon-btn dlog-back" aria-label="Back" onClick={() => navigate("/dashboard")}>
          {Ic.back}
        </button>
        <div className="dlog-tb-id">
          <span
            className="avatar"
            style={{
              background: AVATAR_TONES[horse.colour] || "#5B6470",
              width: 30,
              height: 30,
              fontSize: 12,
            }}
          >
            {horse.name[0]}
          </span>
          <div className="dlog-tb-name-wrap">
            <span className="dlog-tb-name">{horse.name}</span>
            <span className="dlog-tb-sub muted">{horse.breed} · {horse.weightKg}kg</span>
          </div>
        </div>
        <button className="icon-btn" aria-label="More">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="4" cy="8" r="1.3" fill="currentColor" />
            <circle cx="8" cy="8" r="1.3" fill="currentColor" />
            <circle cx="12" cy="8" r="1.3" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ---------- Auto-save indicator -----------------------------------------
function SavedIndicator({ state, since }: { state: "saving" | "saved"; since: number }) {
  if (state === "saving") {
    return (
      <span className="saved-ind saving">
        <span className="dot-pulse"></span>
        Saving…
      </span>
    );
  }
  const label = since < 5 ? "just now" : since < 60 ? `${Math.floor(since)}s ago` : `${Math.floor(since / 60)}m ago`;
  return (
    <span className="saved-ind saved">
      {Ic.check} Saved {label}
    </span>
  );
}

// ---------- Section frame ------------------------------------------------
interface SectionProps {
  num: number;
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  summary?: string;
  children: React.ReactNode;
  hint?: string;
}

function Section({ num, title, icon, defaultOpen = true, summary, children, hint }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`dlog-sec ${open ? "is-open" : "is-closed"}`}>
      <button
        className="dlog-sec-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="dlog-sec-num mono">{String(num).padStart(2, "0")}</span>
        <span className="dlog-sec-icon" aria-hidden="true">{icon}</span>
        <span className="dlog-sec-title">{title}</span>
        {!open && summary && <span className="dlog-sec-summary muted">{summary}</span>}
        <span className="dlog-sec-chev">{Ic.chev}</span>
      </button>
      {open && (
        <div className="dlog-sec-body">
          {children}
          {hint && <p className="dlog-sec-hint muted">{hint}</p>}
        </div>
      )}
    </section>
  );
}

// ---------- Stepper ------------------------------------------------------
interface StepperProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  big?: boolean;
  bigStep?: number;
}

function Stepper({ value, onChange, min = 0, max = 999, step = 1, unit, big = false, bigStep = 5 }: StepperProps) {
  const change = (delta: number) => onChange(Math.max(min, Math.min(max, value + delta)));
  return (
    <div className={`stepper ${big ? "stepper-big" : ""}`}>
      <button className="stepper-btn" onClick={() => change(-step)} aria-label="Decrease">{Ic.minus}</button>
      {big && (
        <button
          className="stepper-bigstep"
          onClick={() => change(-bigStep)}
          aria-label={`Decrease ${bigStep}`}
        >
          −{bigStep}
        </button>
      )}
      <div className="stepper-display">
        <span className="stepper-value mono">{value}</span>
        {unit && <span className="stepper-unit">{unit}</span>}
      </div>
      {big && (
        <button
          className="stepper-bigstep"
          onClick={() => change(bigStep)}
          aria-label={`Increase ${bigStep}`}
        >
          +{bigStep}
        </button>
      )}
      <button className="stepper-btn" onClick={() => change(step)} aria-label="Increase">{Ic.plus}</button>
    </div>
  );
}

// ---------- Sections -----------------------------------------------------
interface SecProps {
  log: LogState;
  update: <K extends keyof LogState>(k: K, v: LogState[K]) => void;
  horse: MockHorse;
}

function HydrationSection({ log, update, horse }: SecProps) {
  const expected = Math.round(horse.targetL * paceFraction(MOCK_NOW_HOUR));
  const pct = horse.targetL > 0 ? Math.min(100, (log.waterL / horse.targetL) * 100) : 0;
  const tickPct = horse.targetL > 0 ? Math.min(100, (expected / horse.targetL) * 100) : 0;
  const label = paceLabel(log.waterL, expected);
  const status = computePace(log.waterL, horse.targetL, MOCK_NOW_HOUR).status;
  return (
    <Section
      num={1}
      title="Hydration"
      icon={Ic.drop}
      defaultOpen={true}
      hint="Estimate is fine; most yard managers eyeball this. Top up through the day."
    >
      <label className="dlog-field-label">Water drunk today</label>
      <Stepper
        value={log.waterL}
        onChange={(v) => update("waterL", v)}
        unit="litres"
        big={true}
        bigStep={5}
        max={120}
      />

      <div className="dlog-pace-preview">
        <div className={`bar ${status}`}>
          <span style={{ width: `${pct}%` }}></span>
          <span className="pace-tick" style={{ left: `${tickPct}%` }}></span>
        </div>
        <div className="dlog-pace-meta mono">
          <span className={`pace-pill pace-tone-${label.tone}`}>{label.text}</span>
          <span className="faint">expected {expected}L · target {horse.targetL}L</span>
        </div>
      </div>
    </Section>
  );
}

interface ConditionsProps {
  log: LogState;
  update: <K extends keyof LogState>(k: K, v: LogState[K]) => void;
  postcode: string | null;
  weatherTempC: number | null;
  weatherLoading: boolean;
  weatherError: string | null;
}

function ConditionsSection({ log, update, postcode, weatherTempC, weatherLoading, weatherError }: ConditionsProps) {
  const hasPostcode = !!postcode;
  const source = !hasPostcode
    ? "Add a postcode in Settings"
    : weatherLoading
      ? "Fetching weather…"
      : weatherError
        ? "Weather unavailable. Override manually."
        : weatherTempC != null
          ? `Live from open-meteo · ${postcode}`
          : `Live weather · ${postcode}`;

  return (
    <Section
      num={2}
      title="Conditions"
      icon={Ic.therm}
      defaultOpen={true}
      hint="Affects the EHE hydration target. We use postcodes.io + open-meteo for the live temp."
    >
      <div className="dlog-temp-row">
        <div className="dlog-temp-amount">
          {log.ambientOverride ? (
            <input
              type="number"
              className="dlog-temp-num mono"
              value={log.ambientTempC ?? 0}
              onChange={(e) => update("ambientTempC", +e.target.value)}
              style={{ width: 90, fontSize: 32, fontWeight: 600, background: "transparent", border: 0, padding: 0, color: "inherit", textAlign: "left" }}
            />
          ) : (
            <span className="dlog-temp-num mono">{log.ambientTempC ?? "—"}</span>
          )}
          <span className="dlog-temp-unit">°C</span>
        </div>
        <div className="dlog-temp-meta">
          {log.ambientOverride ? (
            <>
              <span className="dlog-temp-src">Manual override</span>
              {hasPostcode && !weatherLoading && (
                <button
                  className="link-btn"
                  onClick={() => {
                    update("ambientOverride", false);
                    if (weatherTempC != null) update("ambientTempC", Math.round(weatherTempC));
                  }}
                >
                  Revert to live weather
                </button>
              )}
            </>
          ) : (
            <>
              <span className="dlog-temp-src">
                <span className={`dot ${hasPostcode && !weatherError ? "ambient-fresh" : "idle"}`} aria-hidden="true"></span>
                {source}
              </span>
              <button className="link-btn" onClick={() => update("ambientOverride", true)}>
                {Ic.pencil} Override
              </button>
            </>
          )}
        </div>
      </div>
    </Section>
  );
}

function ExerciseSection({ log, update }: Omit<SecProps, "horse">) {
  const intensities = [
    { k: "light", label: "Light" },
    { k: "medium", label: "Medium" },
    { k: "intense", label: "Intense" },
  ];
  return (
    <Section num={3} title="Exercise" icon={Ic.exer} defaultOpen={true}>
      <label className="dlog-field-label">Duration</label>
      <Stepper
        value={log.exercise.mins}
        onChange={(v) => update("exercise", { ...log.exercise, mins: v })}
        unit="min"
        big={true}
        bigStep={15}
        step={5}
        max={240}
      />
      {log.exercise.mins > 0 && (
        <>
          <label className="dlog-field-label dlog-field-label-spaced">Intensity</label>
          <div className="dlog-segmented">
            {intensities.map((o) => (
              <button
                key={o.k}
                className={`dlog-seg ${log.exercise.intensity === o.k ? "active" : ""}`}
                onClick={() => update("exercise", { ...log.exercise, intensity: o.k })}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

function FeedSection({ log, update }: Omit<SecProps, "horse">) {
  const summary = log.feed.map((f) => `${f.kg}kg ${f.type}`).join(" · ");
  return (
    <Section num={4} title="Feed" icon={Ic.feed} defaultOpen={false} summary={summary}>
      <div className="dlog-baseline-row">
        <button
          className={`dlog-toggle ${log.feedSameAsBaseline ? "active" : ""}`}
          onClick={() => update("feedSameAsBaseline", !log.feedSameAsBaseline)}
          aria-pressed={log.feedSameAsBaseline}
        >
          <span className="dlog-toggle-tick">{log.feedSameAsBaseline ? Ic.check : null}</span>
          Same as baseline
        </button>
      </div>
      <div className="dlog-feed-list">
        {log.feed.map((f, i) => (
          <div key={i} className="dlog-feed-row">
            <span className="dlog-feed-type">{f.type}</span>
            <Stepper
              value={f.kg}
              onChange={(v) => {
                const next = [...log.feed];
                next[i] = { ...f, kg: v };
                update("feed", next);
                update("feedSameAsBaseline", false);
              }}
              unit="kg"
              step={0.5}
              max={40}
            />
          </div>
        ))}
        <button className="dlog-add-feed">+ Add feed type</button>
      </div>
    </Section>
  );
}

interface TagOption {
  k: string;
  label: string;
  tone: string;
}

interface TagPickerProps {
  options: TagOption[];
  value: string | null;
  onChange: (v: string) => void;
  cols?: number;
}

function TagPicker({ options, value, onChange, cols = 3 }: TagPickerProps) {
  return (
    <div className="dlog-tags" style={{ ["--cols" as string]: cols } as React.CSSProperties}>
      {options.map((o) => {
        const active = value === o.k;
        return (
          <button
            key={o.k}
            className={`dlog-tag tone-${o.tone} ${active ? "active" : ""}`}
            onClick={() => onChange(o.k)}
            aria-pressed={active}
          >
            <span className={`dot ${o.tone}`}></span>
            <span className="dlog-tag-label">{o.label}</span>
            {active && <span className="dlog-tag-tick">{Ic.check}</span>}
          </button>
        );
      })}
    </div>
  );
}

function BehaviourSection({ log, update, horse }: SecProps) {
  const opts: TagOption[] = [
    { k: "bright",     label: "Bright",     tone: "hydrated" },
    { k: "normal",     label: "Normal",     tone: "hydrated" },
    { k: "off",        label: "Off",        tone: "mild" },
    { k: "dull",       label: "Dull",       tone: "mild" },
    { k: "restless",   label: "Restless",   tone: "severe" },
    { k: "distressed", label: "Distressed", tone: "severe" },
  ];
  return (
    <Section
      num={5}
      title="Behaviour"
      icon={Ic.behav}
      defaultOpen={true}
      hint={`How is ${horse.name} today? Pick the closest match.`}
    >
      <TagPicker options={opts} value={log.behaviour} onChange={(v) => update("behaviour", v)} cols={3} />
    </Section>
  );
}

function DroppingsSection({ log, update }: Omit<SecProps, "horse">) {
  const opts: TagOption[] = [
    { k: "normal",     label: "Normal",        tone: "hydrated" },
    { k: "soft-loose", label: "Soft / loose",  tone: "mild" },
    { k: "hard-dry",   label: "Hard / dry",    tone: "mild" },
    { k: "none-seen",  label: "None seen",     tone: "severe" },
  ];
  return (
    <Section
      num={6}
      title="Droppings"
      icon={Ic.drops}
      defaultOpen={true}
      hint="Reduced or absent droppings are an early colic sign."
    >
      <TagPicker
        options={opts}
        value={log.droppings.tag}
        onChange={(v) => update("droppings", { ...log.droppings, tag: v })}
        cols={2}
      />
      <div className="dlog-droppings-count">
        <label className="dlog-field-label dlog-field-label-spaced">
          Piles spotted today (optional)
        </label>
        <Stepper
          value={log.droppings.count ?? 0}
          onChange={(v) => update("droppings", { ...log.droppings, count: v })}
          unit="piles"
          max={20}
        />
      </div>
    </Section>
  );
}

function NotesSection({ log, update }: Omit<SecProps, "horse">) {
  return (
    <Section num={7} title="Notes" icon={Ic.notes} defaultOpen={true}>
      <textarea
        className="dlog-notes"
        rows={3}
        placeholder="Anything unusual? (lameness, swelling, change in routine…)"
        value={log.notes}
        onChange={(e) => update("notes", e.target.value)}
      />
    </Section>
  );
}

function StickyCTA({ isUpdate, onSubmit, submitting }: { isUpdate: boolean; onSubmit: () => void; submitting: boolean }) {
  return (
    <div className="dlog-cta-wrap">
      <button className="btn primary lg dlog-cta" onClick={onSubmit} disabled={submitting}>
        {submitting ? "Saving…" : <>{isUpdate ? "Update log & see hydration" : "Save log & see hydration"} →</>}
      </button>
    </div>
  );
}

// Suppress unused-import warning for currentHour until wiring uses it.
void currentHour;

export default function DailyLog() {
  const { horseId } = useParams<{ horseId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, isDemo } = useCurrentUser();
  const { yard, loading: yardLoading } = useUserYard(user?.uid);
  const { horse: realHorse, loading: realHorseLoading } = useHorse(yard?.id, horseId);
  const { log: existingLog } = useDailyLog(yard?.id, horseId, todayId());

  const activePostcode = isDemo ? MOCK_YARD.postcode : yard?.postcode ?? null;
  const { tempC: weatherTempC, loading: weatherLoading, error: weatherError } = useWeather(activePostcode);

  // The display horse — demo uses the curated mock; non-demo wraps the
  // Firestore horse with whatever is already logged today (if anything).
  const horse = useMemo<MockHorse | undefined>(() => {
    if (isDemo) return MOCK_HORSES.find((h) => h.id === horseId);
    return realHorse ? horseToMock(realHorse, existingLog) : undefined;
  }, [horseId, isDemo, realHorse, existingLog]);

  // Hooks must run unconditionally — never return before them.
  const [log, setLog] = useState<LogState>(() =>
    isDemo
      ? initialLogFor(MOCK_HORSES.find((h) => h.id === horseId))
      : initialLogFromReal(realHorse, existingLog)
  );
  const update = <K extends keyof LogState>(k: K, v: LogState[K]) =>
    setLog((s) => ({ ...s, [k]: v }));

  // Re-init the form once the real horse + existing log arrive from Firestore.
  const [hydrated, setHydrated] = useState<boolean>(isDemo);
  useEffect(() => {
    if (isDemo || hydrated) return;
    if (realHorse) {
      setLog(initialLogFromReal(realHorse, existingLog));
      setHydrated(true);
    }
  }, [isDemo, hydrated, realHorse, existingLog]);

  const [save, setSave] = useState<{ state: "saving" | "saved"; changedAt: number }>({
    state: "saved",
    changedAt: Date.now() - 4000,
  });
  useEffect(() => {
    setSave({ state: "saving", changedAt: Date.now() });
    const t = setTimeout(() => setSave({ state: "saved", changedAt: Date.now() }), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log]);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Auto-pull live weather into the form unless the user overrode it manually.
  useEffect(() => {
    if (weatherTempC == null) return;
    if (log.ambientOverride) return;
    const rounded = Math.round(weatherTempC);
    if (log.ambientTempC !== rounded) {
      setLog((s) => ({ ...s, ambientTempC: rounded }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherTempC, log.ambientOverride]);

  // Loading window — only for non-demo while Firestore hydrates.
  if (!isDemo && (yardLoading || realHorseLoading)) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)", color: "var(--ink-3)", fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  if (!horse) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)" }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "40px 32px", textAlign: "center", maxWidth: 420 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Horse not found</h1>
          <p style={{ color: "var(--ink-3)", fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
            This horse isn't on your yard.
          </p>
          <button className="btn ghost" onClick={() => navigate("/dashboard")}>← Back to dashboard</button>
        </div>
      </div>
    );
  }

  const since = Math.max(0, (Date.now() - save.changedAt) / 1000);
  const isUpdate = !!horse.today || !!existingLog;

  const submit = async () => {
    if (isDemo) {
      navigate(`/horses/${horse.id}?logged=1`);
      return;
    }
    if (!user?.uid || !yard?.id || !realHorse) {
      setSubmitError("Not ready yet. Try again in a moment.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await saveDailyLog({
        yardId: yard.id,
        horseId: realHorse.id,
        dateId: todayId(),
        userId: user.uid,
        horse: realHorse,
        values: {
          waterL: log.waterL,
          ambientTempC: log.ambientTempC,
          exercise: {
            mins: log.exercise.mins,
            intensity: (log.exercise.intensity || "light") as Intensity,
          },
          feed: log.feed,
          behaviour: log.behaviour,
          droppings: {
            tag: (log.droppings.tag ?? null) as DailyLogDoc["droppings"]["tag"],
            count: log.droppings.count,
          },
          notes: log.notes,
        },
      });
      navigate(`/horses/${realHorse.id}?logged=1`);
    } catch (err) {
      console.error("saveDailyLog failed:", err);
      setSubmitError("Couldn't save the log. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`dlog ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <DLogTopBar horse={horse} />
      <main className="dlog-page">
        <div className="dlog-shell">
          <header className="dlog-head">
            <div className="dlog-head-left">
              <h1 className="dlog-h1">{isUpdate ? "Update today's log" : "Log today"}</h1>
              <p className="dlog-h-sub">
                <span>{MOCK_YARD.date} · 10:42am</span>
                <span className="dot-sep">·</span>
                <SavedIndicator state={save.state} since={since} />
              </p>
            </div>
            {!isMobile && (
              <button className="btn primary dlog-cta-top" onClick={submit} disabled={submitting}>
                {submitting ? "Saving…" : <>{isUpdate ? "Update log & see hydration" : "Save log & see hydration"} →</>}
              </button>
            )}
          </header>

          {submitError && (
            <div
              role="alert"
              style={{
                background: "var(--status-severe-soft)",
                color: "#8C1818",
                border: "1px solid var(--status-severe-soft)",
                borderRadius: "var(--r-md)",
                padding: "12px 14px",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {submitError}
            </div>
          )}

          <HydrationSection log={log} update={update} horse={horse} />
          <ConditionsSection
            log={log}
            update={update}
            postcode={activePostcode}
            weatherTempC={weatherTempC}
            weatherLoading={weatherLoading}
            weatherError={weatherError}
          />
          <ExerciseSection log={log} update={update} />
          <FeedSection log={log} update={update} />
          <BehaviourSection log={log} update={update} horse={horse} />
          <DroppingsSection log={log} update={update} />
          <NotesSection log={log} update={update} />

          <p className="dlog-foot-note muted">
            Logged by {MOCK_YARD.manager} · auto-saved continuously. Last log will be replaced on save.
          </p>
        </div>
      </main>
      {isMobile && <StickyCTA isUpdate={isUpdate} onSubmit={submit} submitting={submitting} />}
    </div>
  );
}
